import { Close } from '@mui/icons-material';
import {
  Autocomplete,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { searchActiveUsers, UserChip } from '@/tracker/entities/user';
import { useServerSearch } from '@/tracker/hooks/useServerSearch';
import { Queue, User } from '@/tracker/types';
import { Loader } from '@/tracker/ui/Loader';
import { PresenceDialog } from '@/tracker/ui/PresenceDialog';
import { formatDate } from '@/tracker/utils/formatDate';

import { QueueAccessStore } from '../../model/QueueAccessStore';

import s from './QueueAccessDialog.module.scss';

type Props = {
  queue: Queue;
  onClose: () => void;
};

/** Доступ к очереди для админа — как вкладка «Права доступа» в настройках очереди Яндекс Трекера */
const QueueAccessDialog: React.FC<Props> = observer(({ queue, onClose }) => {
  const alertsStore = useAlertsStore();
  const { store } = useLocalStore(() => new QueueAccessStore({ queueKey: queue.key, alertsStore }));
  const [selected, setSelected] = useState<User[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const userSearch = useServerSearch({ search: searchActiveUsers, enabled: isPickerOpen });
  const [memberQuery, setMemberQuery] = useState('');

  useEffect(() => {
    store.load();
  }, [store]);

  // Выбранные держим среди вариантов (их скрывает filterSelectedOptions): иначе MUI считает их невалидными
  const candidates = [
    ...selected,
    ...userSearch.options.filter(
      (user) => !store.memberIds.has(user.id) && !selected.some((item) => item.id === user.id),
    ),
  ];
  const needle = memberQuery.trim().toLowerCase();
  const shownMembers = store.members.filter((member) =>
    member.user.displayName.toLowerCase().includes(needle),
  );

  const add = async () => {
    if (await store.add(selected.map((user) => user.id))) setSelected([]);
  };

  return (
    <PresenceDialog onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Доступ к очереди {queue.key}</DialogTitle>
      <DialogContent className={s.dialog}>
        <Typography variant="body2" color="text.secondary">
          {queue.isPublic
            ? 'Очередь публичная: её задачи видят все сотрудники. Участники сохранят доступ, если её закроют.'
            : 'Задачи очереди видят и меняют только участники и администраторы — в том числе на досках.'}
        </Typography>

        <div className={s.dialog__add}>
          <Autocomplete
            multiple
            size="small"
            fullWidth
            filterSelectedOptions
            options={candidates}
            // Фильтрует сервер
            filterOptions={(all) => all}
            loading={userSearch.isLoading}
            loadingText="Поиск…"
            open={isPickerOpen}
            onOpen={() => setIsPickerOpen(true)}
            onClose={() => {
              setIsPickerOpen(false);
              userSearch.setQuery('');
            }}
            onInputChange={(_, text, reason) => reason === 'input' && userSearch.setQuery(text)}
            value={selected}
            disabled={store.isLoading || store.isError}
            noOptionsText="Никого не нашлось"
            getOptionLabel={(user) => user.displayName}
            // Ключ по id: у разных пользователей бывают одинаковые имена
            renderOption={({ key: _, ...props }, user) => (
              <li key={user.id} {...props}>
                <span>
                  {user.displayName} <span className={s.dialog__login}>{user.username}</span>
                </span>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelected(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selected.length ? '' : 'Сотрудники'}
                slotProps={{
                  htmlInput: { ...params.inputProps, 'aria-label': 'Кому выдать доступ' },
                }}
              />
            )}
          />
          <Button onClick={add} disabled={!selected.length || store.isSaving}>
            Выдать доступ
          </Button>
        </div>

        {store.isLoading && <Loader />}

        {store.isError && (
          <div className={s.dialog__state}>
            <Typography color="text.secondary">Не удалось загрузить участников</Typography>
            <Button onClick={store.load}>Повторить</Button>
          </div>
        )}

        {!store.isLoading && !store.isError && (
          <>
            <div className={s.dialog__membersHeader}>
              <Typography variant="subtitle2">Участники · {store.members.length}</Typography>
              {store.members.length > 0 && (
                <TextField
                  size="small"
                  type="search"
                  placeholder="Поиск по имени"
                  value={memberQuery}
                  onChange={(event) => setMemberQuery(event.target.value)}
                  slotProps={{ htmlInput: { 'aria-label': 'Поиск по участникам' } }}
                />
              )}
            </div>
            {shownMembers.length ? (
              <ul className={s.members}>
                {shownMembers.map(({ user, grantedBy, createTime }) => (
                  <li key={user.id} className={s.members__item}>
                    <UserChip name={user.displayName} />
                    <span className={s.members__granted}>
                      {grantedBy ? `${grantedBy.displayName}, ` : 'импорт, '}
                      {formatDate(createTime)}
                    </span>
                    <IconButton
                      size="small"
                      aria-label={`Забрать доступ у ${user.displayName}`}
                      disabled={store.isSaving}
                      onClick={() => store.remove(user.id)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </li>
                ))}
              </ul>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {store.members.length ? 'Никого не нашлось' : 'Участников нет'}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" onClick={onClose}>
          Готово
        </Button>
      </DialogActions>
    </PresenceDialog>
  );
});

export { QueueAccessDialog };
